/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useRef, useState} from 'react';

import IDate from '@dvrd/idate';

import {ActivateHandle, VATPeriod, VatPeriod} from '../../../util/interfaces';

import {
    getNextVatPeriod,
    getPreviousVatPeriod,
    getVatPeriodLabel,
    getVatPeriods
} from '../../../util/vatDeclarationUtil';

import VatPeriodSelect from './vatPeriodSelect';

import {AppContext} from '../../misc/appContext';

import CustomPeriodController from '../../salesDashboard/customPeriod/customPeriodController';

interface Props {
    onChange: (start: IDate, end: IDate, isCustom?: boolean) => void;
    loading: boolean;
}

const initialPeriodType = VATPeriod.QUARTER;
const maxCustomDate = IDate.now().day(1).subtract(1, 'day');

export default function VatPeriodSelectController(props: Props) {
    const context = useContext(AppContext);
    const {onChange, loading} = props;
    const [periodType, setPeriodType] = useState<VATPeriod>(initialPeriodType);
    const [periods, setPeriods] = useState(getVatPeriods(initialPeriodType));
    const [selectedPeriod, setSelectedPeriod] = useState<string>('');
    const customPeriodRef = useRef<ActivateHandle>(null);

    function onSubmitCustomPeriod(start: IDate, end: IDate) {
        onChange(start, end, true);
    }

    function onPrevPeriod() {
        const _periods = periods.slice();
        const firstPeriod = _periods[0];
        const prevPeriod = getPreviousVatPeriod(periodType, firstPeriod.start);
        _periods.splice(0, 0, {...prevPeriod, label: getVatPeriodLabel(periodType, prevPeriod.start)});
        _periods.pop();
        setPeriods(_periods);
    }

    function onNextPeriod() {
        const _periods = periods.slice();
        const lastPeriod = _periods[_periods.length - 1];
        const nextPeriod = getNextVatPeriod(periodType, lastPeriod.start);
        _periods.push({...nextPeriod, label: getVatPeriodLabel(periodType, nextPeriod.start)});
        _periods.shift();
        setPeriods(_periods);
    }

    function onClickPeriod(period: VatPeriod) {
        return function () {
            if (!loading) {
                const selectLabel = period.label;
                setSelectedPeriod(selectLabel);
                onChange(period.start, period.end);
            }
        };
    }

    function onClickPeriodType(periodType: VATPeriod) {
        return function () {
            setPeriodType(periodType);
            if (periodType === VATPeriod.CUSTOM) return customPeriodRef.current?.activate();
            const periods = getVatPeriods(periodType);
            setPeriods(periods);
            const selected = periods[periods.length - 1];
            const selectedLabel = selected.label;
            setSelectedPeriod(selectedLabel);
        };
    }

    useEffect(() => {
        setSelectedPeriod('');
    }, [context.accountContext.account]);

    return (
        <>
            <VatPeriodSelect
                onClickPeriod={onClickPeriod}
                onClickPeriodType={onClickPeriodType}
                onPrevPeriod={onPrevPeriod}
                onNextPeriod={onNextPeriod}
                periods={periods}
                periodType={periodType}
                selectedPeriod={selectedPeriod}
                loading={loading}
            />
            <CustomPeriodController ref={customPeriodRef} onSubmit={onSubmitCustomPeriod} max={maxCustomDate.clone()} />
        </>
    );
}
